// Click funnel section — GA4 aggregate button/section tracking + drop-off
import { useCallback, useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { crmFetchGa4ClickAnalyticsCallable, crmBackfillClickAnalyticsCallable } from "../lib/crmApi";
import { ensureCallableAuthContext } from "../lib/crmCallableAuth";
import { CLICK_FUNNEL_STEPS, CLICK_SUPPLEMENTAL_EVENTS } from "../lib/clickAnalyticsEvents";
import {
  computeClickFunnel,
  mergeClickCountMaps,
  countEventsFromFirestore,
} from "../lib/clickAnalyticsMetrics";
import FunnelChart from "./charts/FunnelChart";
import DateRangeFilter from "./charts/DateRangeFilter";
import { format } from "date-fns";
import { resolveDateRange } from "../lib/dateRangeFilter";
import AlertBanner from "./AlertBanner";
import { MousePointerClick, RefreshCw, Database, AlertTriangle } from "lucide-react";

const SETTINGS_REF = doc(db, "crm_settings", "click_analytics");

function ga4StartDate(dateRange, resolvedRange) {
  if (dateRange.preset === "custom" && dateRange.customStart) {
    return dateRange.customStart;
  }
  if (dateRange.preset === "all") return "2024-01-01";
  if (dateRange.preset === "7d") return "7daysAgo";
  if (dateRange.preset === "30d") return "30daysAgo";
  if (dateRange.preset === "year" && resolvedRange.startMs > 0) {
    return format(new Date(resolvedRange.startMs), "yyyy-MM-dd");
  }
  if (dateRange.preset === "month" && resolvedRange.startMs > 0) {
    return format(new Date(resolvedRange.startMs), "yyyy-MM-dd");
  }
  if (dateRange.preset === "week" && resolvedRange.startMs > 0) {
    return format(new Date(resolvedRange.startMs), "yyyy-MM-dd");
  }
  return "30daysAgo";
}

function toCountMap(obj = {}) {
  return new Map(Object.entries(obj).map(([k, v]) => [k, Number(v) || 0]));
}

function fmtStepConv(stage, index) {
  if (index === 0 || stage.stepConvPct == null) return "—";
  return `${stage.stepConvPct.toFixed(1)}%`;
}

function fmtDropOff(stage, index) {
  if (index === 0) return "—";
  if (stage.dropOffPct != null) return `${stage.dropOffPct.toFixed(1)}%`;
  if (stage.stepConvPct != null && stage.stepConvPct > 100) return "—";
  return "0.0%";
}

export default function ClickAnalyticsSection({ productAnalyticsEvents = [] }) {
  const [dateRange, setDateRange] = useState({ preset: "30d" });
  const [ga4Counts, setGa4Counts] = useState(null);
  const [cachedSnapshot, setCachedSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(null);

  useEffect(() => {
    return onSnapshot(SETTINGS_REF, (snap) => {
      setCachedSnapshot(snap.exists() ? snap.data() : null);
    });
  }, []);

  const resolvedRange = useMemo(() => resolveDateRange(dateRange), [dateRange]);

  const loadGa4 = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = await ensureCallableAuthContext();
      if (!auth.ok) {
        setError(auth.message);
        return;
      }
      const startDate = ga4StartDate(dateRange, resolvedRange);
      const endDate =
        dateRange.preset === "custom" && dateRange.customEnd ? dateRange.customEnd : "today";

      const fn = crmFetchGa4ClickAnalyticsCallable();
      const { data } = await fn({ startDate, endDate });
      if (!data?.ok) throw new Error(data?.error || "GA4 fetch failed");
      setGa4Counts(data.countsByEvent || {});
      setSource("ga4");
    } catch (err) {
      setError(err?.message || String(err));
      if (cachedSnapshot?.countsByEvent) {
        setGa4Counts(cachedSnapshot.countsByEvent);
        setSource("cache");
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange, resolvedRange, cachedSnapshot]);

  useEffect(() => {
    loadGa4();
  }, [loadGa4]);

  const countsMap = useMemo(() => {
    const ga4Map = toCountMap(ga4Counts || cachedSnapshot?.countsByEvent || {});
    const fsMap = countEventsFromFirestore(productAnalyticsEvents, {
      startMs: resolvedRange.startMs ?? 0,
      endMs: resolvedRange.endMs ?? Infinity,
    });
    return mergeClickCountMaps(ga4Map, fsMap);
  }, [ga4Counts, cachedSnapshot, productAnalyticsEvents, resolvedRange]);

  const funnel = useMemo(() => computeClickFunnel(countsMap), [countsMap]);

  const funnelChartStages = useMemo(
    () =>
      funnel.mainPathStages.map((stage, i) => ({
        label: stage.label,
        value: stage.value,
        color:
          i === 0
            ? "var(--accent)"
            : stage.instrumented
              ? "rgba(14,165,233,0.75)"
              : "rgba(148,163,184,0.65)",
      })),
    [funnel]
  );

  async function runBackfill() {
    setBackfillBusy(true);
    setError(null);
    try {
      const auth = await ensureCallableAuthContext();
      if (!auth.ok) {
        setError(auth.message);
        return;
      }
      const fn = crmBackfillClickAnalyticsCallable();
      const { data } = await fn({ startDate: "2024-01-01", endDate: "today", runUserBackfill: true });
      if (!data?.ok) throw new Error(data?.error || "Backfill failed");
      if (data.aggregate?.countsByEvent) {
        setGa4Counts(data.aggregate.countsByEvent);
        setSource("ga4");
      }
      await loadGa4();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBackfillBusy(false);
    }
  }

  const lastBackfillLabel = cachedSnapshot?.lastBackfillAt?.toDate
    ? cachedSnapshot.lastBackfillAt.toDate().toLocaleString()
    : cachedSnapshot?.lastBackfillAt
      ? new Date(cachedSnapshot.lastBackfillAt).toLocaleString()
      : null;

  return (
    <section className="analytics-section card">
      <header className="chart-card-header" style={{ marginBottom: 16 }}>
        <div>
          <h2 className="text-h3" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MousePointerClick size={20} style={{ color: "var(--accent)" }} />
            Click tracking
          </h2>
          <p className="page-header-desc" style={{ marginBottom: 0 }}>
            Aggregate button and section clicks from GA4 property{" "}
            <code>507368100</code> (Scanbrix iOS). No per-user assignment — counts only, with step-to-step drop-off.
            {source === "cache" ? " Showing cached snapshot (live GA4 unavailable)." : null}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <DateRangeFilter value={dateRange} onChange={setDateRange} compact />
          <button type="button" className="btn btn-sm" onClick={loadGa4} disabled={loading}>
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={runBackfill} disabled={backfillBusy}>
            <Database size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {backfillBusy ? "Backfilling…" : "Backfill from GA4"}
          </button>
        </div>
      </header>

      {error ? <AlertBanner variant="error">{error}</AlertBanner> : null}

      {funnel.missingInstrumentation.length > 0 ? (
        <AlertBanner variant="info" style={{ marginBottom: 16 }}>
          <strong>Not yet in GA4:</strong> {funnel.missingInstrumentation.join(", ")}. Add{" "}
          <code>Analytics.logEvent</code> in the iOS app (see <code>src/lib/clickAnalyticsLog.js</code>).
        </AlertBanner>
      ) : null}

      <div style={{ marginBottom: 20 }}>
        <div className="section-title" style={{ marginBottom: 8 }}>Main path drop-off</div>
        <p className="text-caption" style={{ marginBottom: 12 }}>
          {resolvedRange.isAllTime ? "All time" : resolvedRange.label}
          {funnel.overallConvPct != null
            ? ` · ${funnel.overallConvPct.toFixed(1)}% signup → order`
            : ""}
          {" · "}Aggregate GA4 click counts — steps are independent events, so conversion can exceed 100% when users skip earlier steps.
        </p>
        <FunnelChart stages={funnelChartStages} />
        <div style={{ overflowX: "auto", marginTop: 16 }}>
          <table className="data-table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Step</th>
                <th style={{ textAlign: "right" }}>Clicks</th>
                <th style={{ textAlign: "right" }}>% of prev step</th>
                <th style={{ textAlign: "right" }}>Drop-off</th>
                <th style={{ textAlign: "left" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {funnel.stages.map((stage, index) => (
                <tr key={stage.key}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{stage.label}</div>
                    <div className="text-caption">{stage.description}</div>
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{stage.value.toLocaleString()}</td>
                  <td style={{ textAlign: "right" }}>{fmtStepConv(stage, index)}</td>
                  <td style={{ textAlign: "right" }}>{fmtDropOff(stage, index)}</td>
                  <td>
                    {!stage.instrumented ? (
                      <span style={{ color: "var(--amber)", fontSize: 12 }}>pending</span>
                    ) : stage.value > 0 ? (
                      <span style={{ color: "var(--green)", fontSize: 12 }}>tracking</span>
                    ) : (
                      <span className="text-caption">no clicks</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-title" style={{ marginBottom: 8 }}>Event breakdown</div>
      <div style={{ overflowX: "auto" }}>
        <table className="data-table" style={{ width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Step</th>
              <th style={{ textAlign: "left" }}>GA4 event</th>
              <th style={{ textAlign: "right" }}>Clicks</th>
              <th style={{ textAlign: "left" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {CLICK_FUNNEL_STEPS.flatMap((step) =>
              step.events.map((ev) => {
                const count = countsMap.get(ev) || 0;
                const inGa4 = step.instrumented || count > 0;
                return (
                  <tr key={`${step.key}-${ev}`}>
                    <td>{step.label}</td>
                    <td>
                      <code style={{ fontSize: 12 }}>{ev}</code>
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{count.toLocaleString()}</td>
                    <td>
                      {inGa4 ? (
                        <span style={{ color: "var(--green)", fontSize: 12 }}>tracking</span>
                      ) : (
                        <span style={{ color: "var(--amber)", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <AlertTriangle size={12} /> needs instrumentation
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
            {CLICK_SUPPLEMENTAL_EVENTS.map((row) => {
              const count = countsMap.get(row.event) || 0;
              return (
                <tr key={`supp-${row.event}`}>
                  <td>
                    {row.stepLabel}
                    <div className="text-caption">{row.note}</div>
                  </td>
                  <td>
                    <code style={{ fontSize: 12 }}>{row.event}</code>
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{count.toLocaleString()}</td>
                  <td>
                    <span className="text-caption">supplemental</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {lastBackfillLabel ? (
        <p className="text-caption" style={{ marginTop: 16 }}>
          Last GA4 backfill: {lastBackfillLabel}
          {cachedSnapshot?.lastBackfillStartDate
            ? ` · range ${cachedSnapshot.lastBackfillStartDate} → ${cachedSnapshot.lastBackfillEndDate || "today"}`
            : ""}
        </p>
      ) : null}
    </section>
  );
}
